import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { formatAmountInPounds } from "util/formatNumber";

import { WASTE_AMOUNT_TOOLTIP, WasteStreamAmounts } from "./wasteStreamAmount";

/**
 * Header for the consolidated "Amount" column, with a tooltip explaining how
 * the value is derived. Shared across the tenant and facility waste grids.
 */
export function WasteStreamAmountHeader() {
  return (
    <Tooltip title={WASTE_AMOUNT_TOOLTIP} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {/* Match the default MuiDataGrid column header weight so this header
            doesn't read as lighter than the others. */}
        <Box component="span" sx={{ fontWeight: 500 }}>
          Amount
        </Box>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
}

type WasteStreamAmountCellProps = WasteStreamAmounts & {
  /**
   * The server-computed effective amount (per-line e-Manifest → vendor →
   * reviewed document). Never recompute this client-side: the rule is
   * per-line, and only stream-level per-source sums are available here.
   */
  amount: number | null | undefined;
};

/**
 * Cell renderer for the consolidated "Amount" column. Displays the effective
 * amount. The per-source breakdown tooltip is reserved for cases where the
 * reported sources actually disagree — when every present source reports the
 * same value, there's no useful information to surface on hover.
 */
export function WasteStreamAmountCell({
  amount,
  vendorAmount,
  eManifestAmount,
  documentAmount,
}: WasteStreamAmountCellProps) {
  const display = formatAmountInPounds(amount);
  if (!display) return null;

  // Only break down sources that actually reported an amount — a blank source
  // is noise, and the breakdown only adds information when sources disagree.
  const reportedSources = [
    { label: "Vendor", amount: vendorAmount, display: formatAmountInPounds(vendorAmount) },
    {
      label: "e-Manifest",
      amount: eManifestAmount,
      display: formatAmountInPounds(eManifestAmount),
    },
    { label: "Document", amount: documentAmount, display: formatAmountInPounds(documentAmount) },
  ].filter((s) => s.display);

  // Sources can unanimously agree yet still disagree with the displayed
  // amount: the effective amount blends per-line, so e.g. two lines
  // (eManifest 100/vendor 0 and eManifest 0/vendor 100) both sum to 100 on
  // every source while the displayed amount is 200. Show the tooltip in that
  // case too.
  const distinctValues = new Set(reportedSources.map((s) => s.amount));
  const [unanimousValue] = distinctValues;
  if (distinctValues.size < 2 && unanimousValue === amount) return <span>{display}</span>;

  const tooltip = (
    <Stack spacing={0.25}>
      {reportedSources.map((s) => (
        <Typography key={s.label} variant="caption">
          {s.label}: {s.display}
        </Typography>
      ))}
      <Typography variant="caption" sx={{ fontStyle: "italic" }}>
        Showing the amount we&apos;ll report: e-Manifest takes priority per line because it&apos;s
        what we report; vendor, then reviewed document amounts fill gaps.
      </Typography>
    </Stack>
  );

  return (
    <Tooltip title={tooltip}>
      <span>{display}</span>
    </Tooltip>
  );
}
